import { useState, useCallback, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useStore } from '../store';
import { VALIDATION } from '@spellion/shared';
import { useTranslation } from '../hooks/useTranslation';
import { PasswordInput } from '../components/common/PasswordInput';

/**
 * LoginPage Component
 *
 * User login page with email and password form.
 * Implements complete login flow with validation, API integration, and error handling.
 * Supports 2FA verification via email code for admin users.
 *
 * @validates Requirements 7.1, 7.2, 7.3, 7.6, 7.7, 14.4
 */
export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const login = useStore((state) => state.login);
  const loadThemeFromServer = useStore((state) => state.loadThemeFromServer);
  const { t } = useTranslation();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFAUserId, setTwoFAUserId] = useState<string | null>(null);
  const [twoFASessionId, setTwoFASessionId] = useState<string | null>(null);
  const [twoFACode, setTwoFACode] = useState(['', '', '', '', '', '']);
  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Email verification state (for unverified login attempts)
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);

  // Validation state
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Check URL params for verification status on mount
  useEffect(() => {
    const verified = searchParams.get('verified');
    const alreadyVerified = searchParams.get('already_verified');
    const errorParam = searchParams.get('error');

    if (verified === 'true') {
      setSuccess(t('login.verification.success'));
    } else if (alreadyVerified === 'true') {
      setSuccess(t('login.verification.alreadyVerified'));
    } else if (errorParam === 'invalid_token') {
      setError(t('login.verification.invalidToken'));
    } else if (errorParam === 'expired_token') {
      setError(t('login.verification.expiredToken'));
    }
  }, [searchParams, t]);

  // Focus first code input when 2FA is required
  useEffect(() => {
    if (requires2FA && codeInputRefs.current[0]) {
      codeInputRefs.current[0].focus();
    }
  }, [requires2FA]);


  const validateEmail = useCallback((value: string): boolean => {
    if (!value.trim()) {
      setEmailError(t('login.email.error.required'));
      return false;
    }
    if (!VALIDATION.EMAIL_REGEX.test(value)) {
      setEmailError(t('login.email.error.invalid'));
      return false;
    }
    setEmailError(null);
    return true;
  }, [t]);

  const validatePassword = useCallback((value: string): boolean => {
    if (!value) {
      setPasswordError(t('login.password.error.required'));
      return false;
    }
    if (value.length < VALIDATION.PASSWORD_MIN_LENGTH) {
      setPasswordError(t('login.password.error.minLength').replace('{min}', String(VALIDATION.PASSWORD_MIN_LENGTH)));
      return false;
    }
    if (value.length > VALIDATION.PASSWORD_MAX_LENGTH) {
      setPasswordError(t('login.password.error.maxLength').replace('{max}', String(VALIDATION.PASSWORD_MAX_LENGTH)));
      return false;
    }
    setPasswordError(null);
    return true;
  }, [t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password, rememberMe }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        if (data.error?.code === 'AUTH_ACCOUNT_LOCKED') {
          setError(t('login.errors.accountLocked'));
        } else if (data.error?.code === 'AUTH_ACCOUNT_DISABLED') {
          setError(t('login.errors.accountDisabled'));
        } else if (data.error?.code === 'AUTH_EMAIL_NOT_VERIFIED') {
          setEmailNotVerified(true);
          setUnverifiedEmail(data.data?.email || email.trim());
          setError(t('login.errors.emailNotVerified'));
        } else {
          setError(t('login.errors.invalidCredentials'));
        }
        return;
      }


      // Handle 2FA requirement
      if (data.data?.requires2FA) {
        setRequires2FA(true);
        setTwoFAUserId(data.data.userId);
        setTwoFASessionId(data.data.sessionId);
        setSuccess(t('login.twoFactor.codeSent'));
        return;
      }

      // Successful login
      if (data.data?.user && data.data?.token) {
        login(data.data.user, data.data.token, rememberMe);
        // Load user's theme preference from server
        await loadThemeFromServer(data.data.token);
        navigate('/');
      } else {
        setError(t('login.errors.serverError'));
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(t('login.errors.serverError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FACodeChange = (index: number, value: string) => {
    // Handle iOS autofill - if we receive the full code, distribute it
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6);
      if (digits.length > 0) {
        const newCode = Array(6).fill('');
        for (let i = 0; i < digits.length; i++) {
          newCode[i] = digits[i];
        }
        setTwoFACode(newCode);
        // Focus the next empty input or the last one if all are filled
        const focusIndex = Math.min(digits.length, 5);
        codeInputRefs.current[focusIndex]?.focus();
        return;
      }
    }

    if (!/^\d*$/.test(value)) return; // Only digits

    const newCode = [...twoFACode];
    newCode[index] = value.slice(-1); // Only keep last digit
    setTwoFACode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handle2FAKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !twoFACode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handle2FAPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    
    // Extract only digits from pasted content
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);
    
    if (digits.length > 0) {
      const newCode = [...twoFACode];
      for (let i = 0; i < digits.length && i < 6; i++) {
        newCode[i] = digits.charAt(i);
      }
      setTwoFACode(newCode);
      
      // Focus the next empty input or the last one if all are filled
      const nextEmptyIndex = newCode.findIndex((d) => !d);
      const focusIndex = nextEmptyIndex === -1 ? 5 : Math.min(nextEmptyIndex, 5);
      codeInputRefs.current[focusIndex]?.focus();
    }
  };


  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const code = twoFACode.join('');
    if (code.length !== 6) {
      setError(t('login.twoFactor.enterFullCode'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/2fa/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: twoFAUserId, sessionId: twoFASessionId, code }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        if (data.error?.code === 'AUTH_ACCOUNT_LOCKED') {
          setError(t('login.errors.accountLocked'));
          // Rediriger vers la page de connexion après 3 secondes
          setTimeout(() => {
            handleBackToLogin();
          }, 3000);
        } else {
          setError(t('login.twoFactor.invalidCode'));
          setTwoFACode(['', '', '', '', '', '']);
          codeInputRefs.current[0]?.focus();
        }
        return;
      }

      if (data.data?.user && data.data?.token) {
        login(data.data.user, data.data.token, rememberMe);
        // Load user's theme preference from server
        await loadThemeFromServer(data.data.token);
        navigate('/');
      }
    } catch (err) {
      console.error('2FA verify error:', err);
      setError(t('login.errors.serverError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/2fa/resend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: twoFAUserId, sessionId: twoFASessionId }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(t('login.twoFactor.newCodeSent'));
        setTwoFACode(['', '', '', '', '', '']);
        codeInputRefs.current[0]?.focus();
      } else {
        setError(t('login.twoFactor.resendError'));
      }
    } catch {
      setError(t('login.errors.serverError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setRequires2FA(false);
    setTwoFAUserId(null);
    setTwoFASessionId(null);
    setTwoFACode(['', '', '', '', '', '']);
    setError(null);
    setSuccess(null);
  };

  const handleResendVerificationEmail = async () => {
    if (!unverifiedEmail) return;
    
    setResendLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: unverifiedEmail }),
      });

      const data = await response.json();

      if (response.status === 429) {
        setError(t('login.errors.serverError'));
      } else if (data.success) {
        if (data.data?.alreadyVerified) {
          setSuccess(t('login.verification.alreadyVerified'));
          setEmailNotVerified(false);
        } else {
          setSuccess(t('login.verification.success'));
        }
      } else {
        setError(t('login.errors.serverError'));
      }
    } catch (err) {
      console.error('Resend verification error:', err);
      setError(t('login.errors.serverError'));
    } finally {
      setResendLoading(false);
    }
  };


  // 2FA Verification Form
  if (requires2FA) {
    return (
      <div className="max-w-md mx-auto py-8">
        <div className="text-center space-y-2 mb-8">
          <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-slate-100">
            {t('login.twoFactor.title')}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {t('login.twoFactor.subtitle')}
          </p>
        </div>

        <form
          className="space-y-6 bg-white dark:bg-slate-800 p-6 md:p-8 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700"
          onSubmit={handle2FASubmit}
        >
          {error && (
            <div role="alert" className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {success && (
            <div role="status" className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400 text-sm">
              {success}
            </div>
          )}

          <div className="flex justify-center gap-2">
            {twoFACode.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (codeInputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                value={digit}
                onChange={(e) => handle2FACodeChange(index, e.target.value)}
                onKeyDown={(e) => handle2FAKeyDown(index, e)}
                onPaste={handle2FAPaste}
                disabled={isLoading}
                className="w-12 h-14 text-center text-2xl font-bold border-2 border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 disabled:opacity-50"
                aria-label={t('login.twoFactor.codeLabel').replace('{number}', String(index + 1))}
              />
            ))}
          </div>

          <button type="submit" disabled={isLoading || twoFACode.join('').length !== 6} className="w-full btn-primary">
            {isLoading ? t('login.twoFactor.submitting') : t('login.twoFactor.submit')}
          </button>

          <div className="flex flex-col gap-2 text-center">
            <button type="button" onClick={handleResendCode} disabled={isLoading}
              className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 disabled:opacity-50">
              {t('login.twoFactor.resend')}
            </button>
            <button type="button" onClick={handleBackToLogin}
              className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300">
              ← {t('login.twoFactor.back')}
            </button>
          </div>
        </form>
      </div>
    );
  }


  // Regular Login Form
  return (
    <div className="max-w-md mx-auto py-8">
      <div className="text-center space-y-2 mb-8">
        <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-slate-100">{t('login.title')}</h1>
        <p className="text-slate-600 dark:text-slate-400">{t('login.subtitle')}</p>
      </div>

      <form className="space-y-6 bg-white dark:bg-slate-800 p-6 md:p-8 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700"
        onSubmit={handleSubmit} noValidate>
        {error && (
          <div role="alert" className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
            <p>{error}</p>
            {emailNotVerified && (
              <button
                type="button"
                onClick={handleResendVerificationEmail}
                disabled={resendLoading}
                className="mt-2 text-red-700 dark:text-red-400 underline hover:no-underline disabled:opacity-50"
              >
                {resendLoading ? t('login.verification.resending') : t('login.verification.resend')}
              </button>
            )}
          </div>
        )}

        {success && (
          <div role="status" className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400 text-sm">
            {success}
          </div>
        )}

        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300">{t('login.email.label')}</label>
          <input type="email" id="email" name="email" autoComplete="email" placeholder={t('login.email.placeholder')}
            className={`input ${emailError ? 'border-red-500' : ''}`}
            value={email} onChange={(e) => { setEmail(e.target.value); setEmailError(null); }}
            onBlur={() => email && validateEmail(email)} disabled={isLoading} required />
          {emailError && <p className="text-sm text-red-600 dark:text-red-400">{emailError}</p>}
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="block text-sm font-medium text-slate-700 dark:text-slate-300">{t('login.password.label')}</label>
          <PasswordInput id="password" name="password" autoComplete="current-password" placeholder={t('login.password.placeholder')}
            hasError={!!passwordError}
            value={password} onChange={(e) => { setPassword(e.target.value); setPasswordError(null); }}
            onBlur={() => password && validatePassword(password)} disabled={isLoading} required />
          {passwordError && <p className="text-sm text-red-600 dark:text-red-400">{passwordError}</p>}
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}
              disabled={isLoading} className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-primary-500 dark:bg-slate-700" />
            <span className="text-sm text-slate-600 dark:text-slate-400">{t('login.rememberMe')}</span>
          </label>
          <Link to="/password-reset-request" className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
            {t('login.forgotPassword')}
          </Link>
        </div>

        <button type="submit" disabled={isLoading} className="w-full btn-primary">
          {isLoading ? t('login.submitting') : t('login.submit')}
        </button>
      </form>

      <p className="text-center mt-6 text-slate-600 dark:text-slate-400">
        {t('login.noAccount')}{' '}
        <Link to="/register" className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
          {t('login.register')}
        </Link>
      </p>
    </div>
  );
}
